/*
 * MDB shim for DataTable
 * Files currently importing this symbol from "mdbreact":
 * - src/Seating/Accounts/accountDetail.jsx
 * - src/Seating/Activities/activities-list.jsx
 * - src/Seating/PurchaseQueue/ShippingDetailsReport.jsx
 * - src/Seating/Reports/accountReport.jsx
 * - src/Seating/Reports/activityReport.jsx
 * - src/Seating/Reports/approvalReport.jsx
 * - src/Seating/Reports/complaintReport.jsx
 * - src/Seating/Reports/deliveryReport.jsx
 * - src/Seating/Reports/expensesReport.jsx
 * - src/Seating/Reports/salesReport.jsx
 * - src/Seating/Reports/serviceOrderReasonReport.jsx
 * - src/Seating/Reports/servicesReport.jsx
 * - src/Seating/Settings/productMatrixEntry.jsx
 * - src/Seating/Settings/productTypes.jsx
 * - src/Seating/Settings/vendor.jsx
 * - src/Seating/Users/thirdPartyUserList.jsx
 */

import React, { useEffect, useMemo, useState } from "react";
import MUITable from "@mui/material/Table";
import MUITableBody from "@mui/material/TableBody";
import MUITableCell from "@mui/material/TableCell";
import MUITableHead from "@mui/material/TableHead";
import MUITableRow from "@mui/material/TableRow";
import MUITableContainer from "@mui/material/TableContainer";
import MUITablePagination from "@mui/material/TablePagination";
import MUITextField from "@mui/material/TextField";
import MUIInputAdornment from "@mui/material/InputAdornment";
import MUISearchIcon from "@mui/icons-material/Search";
import MUITableSortLabel from "@mui/material/TableSortLabel";

export default function DataTable({
  columns = [],
  rows = [],
  data,
  type,
  paginated = false,
  search = false,
  searchPlaceholder = "Search...",
  defaultRowsPerPage = 10,
  rowsPerPageOptions = [10, 25, 50],
  sort = false,
  sortable,
  ...rest
}) {
  const resolvedColumns = columns.length ? columns : data?.columns || [];
  const resolvedRows = rows.length ? rows : data?.rows || [];
  const pagination = { page: 0, pageSize: 10 };
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [query, setQuery] = useState("");

  const sortingEnabled = sort || sortable;

  const defaultSort = useMemo(() => {
    if (!sortingEnabled) {
      return { orderBy: null, order: "asc" };
    }

    const column = resolvedColumns.find(
      (col) => col.sort === "asc" || col.sort === "desc",
    );

    if (!column) {
      return { orderBy: null, order: "asc" };
    }

    return {
      orderBy: column.field || column.label,
      order: column.sort,
    };
  }, [sortingEnabled, resolvedColumns]);

  const [orderBy, setOrderBy] = useState(defaultSort.orderBy);
  const [order, setOrder] = useState(defaultSort.order);

  useEffect(() => {
    if (!sortingEnabled) return;
    setOrderBy(defaultSort.orderBy);
    setOrder(defaultSort.order);
  }, [sortingEnabled, defaultSort.orderBy, defaultSort.order]);

  const getCellValue = (row, column) => {
    const key = column.field || column.label;
    return column.field ? row[column.field] : row[key];
  };

  const compareValues = (a, b) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (typeof a === "number" && typeof b === "number") return a - b;
    if (typeof a === "object" || typeof b === "object") return 0;
    return String(a).localeCompare(String(b), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  };

  const isColumnSortable = (column) => {
    if (!sortingEnabled || column.sortable === false) return false;
    const key = column.field || column.label;
    return Boolean(key && key !== "button");
  };

  const handleSort = (column) => {
    const key = column.field || column.label;
    if (!isColumnSortable(column)) return;

    if (orderBy === key) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(key);
      setOrder("asc");
    }
    setPage(0);
  };

  const renderHeaderCell = (column, index) => {
    const key = column.field || column.label || index;
    const columnKey = column.field || column.label;

    return (
      <MUITableCell
        key={key}
        sortDirection={orderBy === columnKey ? order : false}
      >
        {isColumnSortable(column) ? (
          <MUITableSortLabel
            active={orderBy === columnKey}
            direction={orderBy === columnKey ? order : "asc"}
            onClick={() => handleSort(column)}
          >
            {column.label || column.field || ""}
          </MUITableSortLabel>
        ) : (
          column.label || column.field || ""
        )}
      </MUITableCell>
    );
  };

  const filteredRows = useMemo(() => {
    if (!search || !query.trim()) return resolvedRows;
    const q = query.trim().toLowerCase();
    return resolvedRows.filter((row) =>
      resolvedColumns.some((column) => {
        const key = column.field || column.label;
        if (!key || key === "button") return false;
        const value = column.field ? row[column.field] : row[key];
        if (value == null || typeof value === "object") return false;
        return String(value).toLowerCase().includes(q);
      }),
    );
  }, [search, query, resolvedRows, resolvedColumns]);

  useEffect(() => {
    setPage(0);
  }, [query]);

  useEffect(() => {
    if (!paginated) return;
    setPage((p) => {
      const maxPage = Math.max(
        0,
        Math.ceil(filteredRows.length / rowsPerPage) - 1,
      );
      return Math.min(p, maxPage);
    });
  }, [paginated, filteredRows.length, rowsPerPage]);

  const sortedRows = useMemo(() => {
    if (!sortingEnabled || !orderBy) return filteredRows;

    const column = resolvedColumns.find(
      (col) => (col.field || col.label) === orderBy,
    );
    if (!column) return filteredRows;

    const direction = order === "desc" ? -1 : 1;
    return [...filteredRows].sort((rowA, rowB) => {
      const cmp = compareValues(
        getCellValue(rowA, column),
        getCellValue(rowB, column),
      );
      return cmp * direction;
    });
  }, [sortingEnabled, orderBy, order, filteredRows, resolvedColumns]);

  const visibleRows = paginated
    ? sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : sortedRows;

  if (type === "dataTable") {
    return (
      <MUIPaper>
        <MUIDataGrid
          rows={resolvedRows}
          columns={resolvedColumns}
          pagination={pagination}
          {...rest}
        >
          <MUITableHead>
            <MUITableRow>
              {resolvedColumns.map((column, index) => (
                <MUITableCell key={column.field || column.label || index}>
                  {column.label || column.field || ""}
                </MUITableCell>
              ))}
            </MUITableRow>
          </MUITableHead>
          <MUITableBody>
            {resolvedRows.map((row, rowIndex) => (
              <MUITableRow key={row.id || rowIndex}>
                {resolvedColumns.map((column, colIndex) => {
                  const key = column.field || column.label || colIndex;
                  const value = column.field ? row[column.field] : row[key];
                  return (
                    <MUITableCell key={`${key}-${colIndex}`}>
                      {value}
                    </MUITableCell>
                  );
                })}
              </MUITableRow>
            ))}
          </MUITableBody>
        </MUIDataGrid>
      </MUIPaper>
    );
  }

  const searchBar = search ? (
    <MUITextField
      size="small"
      fullWidth
      placeholder={searchPlaceholder}
      value={query}
      onChange={(e) => setQuery(e.target.value)}
      sx={{ mb: 2, maxWidth: 250 }}
      slotProps={{
        input: {
          startAdornment: (
            <MUIInputAdornment position="start">
              <MUISearchIcon fontSize="small" />
            </MUIInputAdornment>
          ),
        },
      }}
    />
  ) : null;

  const table = (
    <MUITable {...rest}>
      <MUITableHead>
        <MUITableRow>{resolvedColumns.map(renderHeaderCell)}</MUITableRow>
      </MUITableHead>
      <MUITableBody>
        {visibleRows.map((row, rowIndex) => {
          const rowKey =
            row.id != null
              ? row.id
              : paginated
                ? page * rowsPerPage + rowIndex
                : rowIndex;
          return (
            <MUITableRow key={rowKey}>
              {resolvedColumns.map((column, colIndex) => {
                const key = column.field || column.label || colIndex;
                const value = column.field ? row[column.field] : row[key];
                return (
                  <MUITableCell key={`${key}-${colIndex}`}>
                    {value}
                  </MUITableCell>
                );
              })}
            </MUITableRow>
          );
        })}
      </MUITableBody>
    </MUITable>
  );

  if (!paginated) {
    return (
      <>
        {searchBar}
        {table}
      </>
    );
  }

  return (
    <MUITableContainer>
      {searchBar}
      {table}
      <MUITablePagination
        component="div"
        count={filteredRows.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={rowsPerPageOptions}
      />
    </MUITableContainer>
  );
}
